import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";
import { Menu, Shield, Phone, Mail, ChevronDown, Camera, AlertCircle, Fence } from "lucide-react";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import blueKnightLogo from "@/assets/blue-knight-logo.jpg";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4">
        {/* Top contact bar */}
        <div className="hidden md:flex justify-between items-center py-2 text-sm border-b border-border">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">(416) 555-BLUE</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">info@blueknightcontracting.ca</span>
            </div>
          </div>
          <div className="text-muted-foreground">
            Serving All of Ontario - Licensed & Insured
          </div>
        </div>

        {/* Main navigation */}
        <nav className="flex items-center justify-between py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="bg-white p-1 rounded-lg shadow-sm">
              <img 
                src={blueKnightLogo} 
                alt="Blue Knight Contracting Logo" 
                className="h-10 w-10 object-contain"
                loading="eager"
                decoding="async"
                width="40"
                height="40"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Blue Knight</h1>
              <p className="text-sm text-muted-foreground">Contracting & Construction</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-foreground hover:text-primary transition-colors">
              Home
            </Link>
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors outline-none">
                Services
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-background border border-border shadow-lg z-50">
                <DropdownMenuLabel className="text-primary font-semibold">Our Services</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services" className="w-full cursor-pointer">
                    All Services Overview
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/general-contracting" className="w-full cursor-pointer">
                    General Contracting
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/heavy-equipment" className="w-full cursor-pointer">
                    Heavy Equipment & Site Work
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/specialty-contracting" className="w-full cursor-pointer">
                    Specialty Contracting
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/design-engineering" className="w-full cursor-pointer">
                    Design & Engineering Support
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Skilled Trades Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-primary transition-colors outline-none">
                Skilled Trades
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-background border border-border shadow-lg z-50 max-h-[600px] overflow-y-auto">
                <DropdownMenuLabel className="text-primary font-semibold">Core Trades</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/carpenter" className="w-full cursor-pointer">
                    Carpenter Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/electrician" className="w-full cursor-pointer">
                    Electrician Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/plumber" className="w-full cursor-pointer">
                    Plumbing Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/hvac" className="w-full cursor-pointer">
                    HVAC Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/masonry" className="w-full cursor-pointer">
                    Masonry Services
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Exterior & Site Work</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/roofing" className="w-full cursor-pointer">
                    Roofing Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/asphalt-paving-concrete" className="w-full cursor-pointer">
                    Asphalt, Paving & Concrete
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/welding" className="w-full cursor-pointer">
                    Welding & Fabrication
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Interior Finishing</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/drywall" className="w-full cursor-pointer">
                    Drywall Finishing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/flooring" className="w-full cursor-pointer">
                    Flooring & Tile Installation
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/painting" className="w-full cursor-pointer">
                    Painting Services
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/windows-doors" className="w-full cursor-pointer">
                    Windows & Doors Installation
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Renovations</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/bathroom-renovations" className="w-full cursor-pointer">
                    Bathroom Renovations
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/services/kitchen-renovations" className="w-full cursor-pointer">
                    Kitchen Renovations
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Emergency Services</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/restoration" className="w-full cursor-pointer">
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Restoration & Insurance
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Specialty Inspections</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/drone-inspections" className="w-full cursor-pointer">
                    <Camera className="mr-2 h-4 w-4" />
                    Drone Inspections
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator className="my-2" />
                <DropdownMenuLabel className="text-primary font-semibold">Outdoor Construction</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/services/deck-fence" className="w-full cursor-pointer">
                    <Fence className="mr-2 h-4 w-4" />
                    Deck & Fence Installation
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/gallery" className="text-foreground hover:text-primary transition-colors">
              Gallery
            </Link>
            <Link to="/locations" className="text-foreground hover:text-primary transition-colors">
              Service Areas
            </Link>
            <Link to="/about" className="text-foreground hover:text-primary transition-colors">
              About Us
            </Link>
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors">
              Contact
            </Link>
            <Link to="/careers" className="text-foreground hover:text-primary transition-colors">
              Careers
            </Link>
            <Link to="/blog" className="text-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Button variant="cta" size="lg">
              Free Quote
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
          >
            <Menu className="h-6 w-6" />
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border py-4">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">
                Home
              </Link>
              
              {/* Mobile Services Dropdown */}
              <div className="space-y-2">
                <div className="text-foreground font-medium">Services</div>
                <div className="ml-4 space-y-2">
                  <Link to="/services" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    All Services Overview
                  </Link>
                  <Link to="/services/general-contracting" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    General Contracting
                  </Link>
                  <Link to="/services/heavy-equipment" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Heavy Equipment & Site Work
                  </Link>
                  <Link to="/services/specialty-contracting" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Specialty Contracting
                  </Link>
                  <Link to="/services/design-engineering" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Design & Engineering Support
                  </Link>
                </div>
              </div>

              {/* Mobile Skilled Trades Dropdown */}
              <div className="space-y-2">
                <div className="text-foreground font-medium">Skilled Trades</div>
                <div className="ml-4 space-y-3">
                  {/* Core Trades */}
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-primary uppercase">Core Trades</div>
                    <Link to="/services/carpenter" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Carpenter Services
                    </Link>
                    <Link to="/services/electrician" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Electrician Services
                    </Link>
                    <Link to="/services/plumber" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Plumbing Services
                    </Link>
                    <Link to="/services/hvac" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      HVAC Services
                    </Link>
                    <Link to="/services/masonry" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Masonry Services
                    </Link>
                  </div>
                  
                  {/* Exterior & Site Work */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Exterior & Site Work</div>
                    <Link to="/services/roofing" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Roofing Services
                    </Link>
                    <Link to="/services/asphalt-paving-concrete" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Asphalt, Paving & Concrete
                    </Link>
                    <Link to="/services/welding" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Welding & Fabrication
                    </Link>
                  </div>
                  
                  {/* Interior Finishing */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Interior Finishing</div>
                    <Link to="/services/drywall" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Drywall Finishing
                    </Link>
                    <Link to="/services/flooring" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Flooring & Tile Installation
                    </Link>
                    <Link to="/services/painting" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Painting Services
                    </Link>
                    <Link to="/services/windows-doors" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Windows & Doors Installation
                    </Link>
                  </div>
                  
                  {/* Renovations */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Renovations</div>
                    <Link to="/services/bathroom-renovations" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Bathroom Renovations
                    </Link>
                    <Link to="/services/kitchen-renovations" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Kitchen Renovations
                    </Link>
                  </div>
                  
                  {/* Emergency Services */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Emergency Services</div>
                    <Link to="/services/restoration" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Restoration & Insurance
                    </Link>
                  </div>
                  
                  {/* Specialty Inspections */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Specialty Inspections</div>
                    <Link to="/services/drone-inspections" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Drone Inspections
                    </Link>
                  </div>
                  
                  {/* Outdoor Construction */}
                  <div className="space-y-1 pt-2 border-t border-border">
                    <div className="text-xs font-semibold text-primary uppercase">Outdoor Construction</div>
                    <Link to="/services/deck-fence" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                      Deck & Fence Installation
                    </Link>
                  </div>
                </div>
              </div>
              
              <Link to="/gallery" className="text-foreground hover:text-primary transition-colors">
                Gallery
              </Link>
              <Link to="/locations" className="text-foreground hover:text-primary transition-colors">
                Service Areas
              </Link>
              <Link to="/about" className="text-foreground hover:text-primary transition-colors">
                About Us
              </Link>
              <Link to="/contact" className="text-foreground hover:text-primary transition-colors">
                Contact
              </Link>
              <Link to="/careers" className="text-foreground hover:text-primary transition-colors">
                Careers
              </Link>
              <Link to="/blog" className="text-foreground hover:text-primary transition-colors">
                Blog
              </Link>
              <Button variant="cta" size="lg" className="w-full">
                Free Quote
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;